import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CHANNEL_STATUS, IChannel, PROTOCOL } from '@/entities/channel';
import { isHttpsPaymentLink } from '@/features/billing/model/paymentRedirect';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { IColumn, IDataRow, Table } from '@/shared/ui/Table';
import { CustomTypography } from '@/shared/ui/Typography';

import { canAuthorizeChannelProtocol } from '../../model/channelAuthorization';
import { getChannelPrimaryAction } from '../../model/channelListStatus';
import { useChannelName } from '../../model/useChannelName';
import { useChannelSubscriptionState } from '../../model/useChannelSubscriptionState';
import { AvitoConnectForm } from '../avito/AvitoConnectForm';
import { ChannelIconByType } from '../ChannelIconByType';
import { ChannelStatus } from '../ChannelStatus';
import { CreateTgBotDialog } from '../CreateTgBotDialog';
import { MaxAuthDialog } from '../max/MaxAuthDialog';
import { MaxPersonalAuthDialog } from '../maxPersonal/MaxPersonalAuthDialog';
import { OzonConnectForm } from '../ozon/OzonConnectForm';
import { TelegramPersonalAuthDialog } from '../telegramPersonal/TelegramPersonalAuthDialog';
import { VkAuthDialog } from '../vk/VkAuthDialog';
import { WhatsAppPersonalAuthDialog } from '../whatsappPersonal/WhatsAppPersonalAuthDialog';
import { WildberriesConnectForm } from '../wildberries/WildberriesConnectForm';

import { ActiveSwitcher } from './ActiveSwitcher';
import { ListItemButton } from './ListItemButton';

type TChannelsListProps = {
  listNotFoundActionSlot?: React.ReactNode;
  channels: IChannel[] | undefined;
  isPending: boolean;
  renderActions: (channel: IChannel) => React.ReactNode;
  renderTariff: (channel: IChannel) => React.ReactNode;
  onPaySubscription?: (channel: IChannel) => void;
  onRenewSubscription?: (channel: IChannel) => void;
};

const COLUMN_WIDTHS = {
  type: { width: '6%' },
  channelId: { width: '15%' },
  name: { width: '24%' },
  status: { width: '14%' },
  subscription: { width: '18%' },
  authorize: { width: '13%' },
  isActive: { width: '5%' },
  actions: { width: '5%' },
} as const;

const getColumns = (t: (key: string) => string): IColumn[] => [
  { key: 'type', label: t('channels:listTable.type'), sx: COLUMN_WIDTHS.type },
  {
    key: 'channelId',
    label: t('channels:listTable.channelId'),
    sx: COLUMN_WIDTHS.channelId,
  },
  {
    key: 'name',
    label: t('channels:listTable.name'),
    sx: COLUMN_WIDTHS.name,
  },
  {
    key: 'status',
    label: t('channels:listTable.status'),
    sx: COLUMN_WIDTHS.status,
  },
  {
    key: 'subscription',
    label: t('channels:listTable.tariff'),
    sx: COLUMN_WIDTHS.subscription,
  },
  { key: 'authorize', label: '', sx: COLUMN_WIDTHS.authorize },
  {
    key: 'isActive',
    label: '',
    sx: COLUMN_WIDTHS.isActive,
  },
  { key: 'actions', label: '', sx: COLUMN_WIDTHS.actions },
];

export const ChannelsList = ({
  channels,
  isPending,
  listNotFoundActionSlot,
  renderActions,
  renderTariff,
  onPaySubscription,
  onRenewSubscription,
}: TChannelsListProps) => {
  const { t } = useTranslation();

  const [openIsWhatsAppQrDialog, setOpenIsWhatsAppQrDialog] = useState(false);
  const [openIsTgBotTokenDialog, setOpenIsTgBotTokenDialog] = useState(false);
  const [openIsMaxBotTokenDialog, setOpenIsMaxBotTokenDialog] = useState(false);
  const [openIsMaxPersonalDialog, setOpenIsMaxPersonalDialog] = useState(false);
  const [openIsAvitoDialog, setOpenIsAvitoDialog] = useState(false);
  const [openIsWildberriesDialog, setOpenIsWildberriesDialog] = useState(false);
  const [openIsOzonDialog, setOpenIsOzonDialog] = useState(false);
  const [openIsVkDialog, setOpenIsVkDialog] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<IChannel | null>(null);
  const [
    openIsTgPersonalAuthMethodDialog,
    setOpenIsTgPersonalAuthMethodDialog,
  ] = useState(false);

  const { getChannelName } = useChannelName();

  const handleCloseWhatsAppQrDialog = () => {
    setSelectedChannel(null);
    setOpenIsWhatsAppQrDialog(false);
  };

  const handleOpenTgBotTokenDialog = (channel: IChannel) => {
    setSelectedChannel(channel);
    setOpenIsTgBotTokenDialog(true);
  };

  const handleCloseTgBotTokenDialog = () => {
    setSelectedChannel(null);
    setOpenIsTgBotTokenDialog(false);
  };

  const handleOpenMaxBotTokenDialog = (channel: IChannel) => {
    setSelectedChannel(channel);
    setOpenIsMaxBotTokenDialog(true);
  };

  const handleCloseMaxBotTokenDialog = () => {
    setSelectedChannel(null);
    setOpenIsMaxBotTokenDialog(false);
  };

  const handleOpenMaxPersonalDialog = (channel: IChannel) => {
    setSelectedChannel(channel);
    setOpenIsMaxPersonalDialog(true);
  };

  const handleCloseMaxPersonalDialog = () => {
    setSelectedChannel(null);
    setOpenIsMaxPersonalDialog(false);
  };

  const handleOpenAvitoDialog = (channel: IChannel) => {
    setSelectedChannel(channel);
    setOpenIsAvitoDialog(true);
  };

  const handleCloseAvitoDialog = () => {
    setSelectedChannel(null);
    setOpenIsAvitoDialog(false);
  };

  const handleOpenWildberriesDialog = (channel: IChannel) => {
    setSelectedChannel(channel);
    setOpenIsWildberriesDialog(true);
  };

  const handleCloseWildberriesDialog = () => {
    setSelectedChannel(null);
    setOpenIsWildberriesDialog(false);
  };

  const handleOpenOzonDialog = (channel: IChannel) => {
    setSelectedChannel(channel);
    setOpenIsOzonDialog(true);
  };

  const handleCloseOzonDialog = () => {
    setSelectedChannel(null);
    setOpenIsOzonDialog(false);
  };

  const handleOpenVkDialog = (channel: IChannel) => {
    setSelectedChannel(channel);
    setOpenIsVkDialog(true);
  };

  const handleCloseVkDialog = () => {
    setSelectedChannel(null);
    setOpenIsVkDialog(false);
  };

  const handleCloseTgPersonalAuthMethodDialog = () => {
    setSelectedChannel(null);
    setOpenIsTgPersonalAuthMethodDialog(false);
  };

  const handleAuthorizeTelegramPersonalButtonClick = (channel: IChannel) => {
    setSelectedChannel(channel);
    setOpenIsTgPersonalAuthMethodDialog(true);
  };

  const handleAuthorizeWhatsAppButtonClick = (channel: IChannel) => {
    setSelectedChannel(channel);
    setOpenIsWhatsAppQrDialog(true);
  };

  if (!channels && isPending) {
    return <Loader containerSize="medium" />;
  }

  if (!channels) {
    return (
      <NotFoundData
        title={t('channels:channelsListNotFound')}
        caption={t('channels:channelsListNotFoundCaption')}
        actionSlot={listNotFoundActionSlot}
      />
    );
  }

  const handleAuthButtonClick = (channel: IChannel) => {
    switch (channel.protocol) {
      case PROTOCOL.TELEGRAM_BOT: {
        handleOpenTgBotTokenDialog(channel);
        break;
      }
      case PROTOCOL.WHATSAPP_GRAY: {
        handleAuthorizeWhatsAppButtonClick(channel);
        break;
      }
      case PROTOCOL.TELEGRAM_PERSONAL: {
        handleAuthorizeTelegramPersonalButtonClick(channel);
        break;
      }
      case PROTOCOL.MAX: {
        handleOpenMaxBotTokenDialog(channel);
        break;
      }
      case PROTOCOL.MAX_PERSONAL: {
        handleOpenMaxPersonalDialog(channel);
        break;
      }
      case PROTOCOL.AVITO: {
        handleOpenAvitoDialog(channel);
        break;
      }
      case PROTOCOL.WILDBERRIES: {
        handleOpenWildberriesDialog(channel);
        break;
      }
      case PROTOCOL.OZON: {
        handleOpenOzonDialog(channel);
        break;
      }
      case PROTOCOL.VK: {
        handleOpenVkDialog(channel);
        break;
      }
      default: {
        break;
      }
    }
  };

  const data: IDataRow[] = channels.map((channel) => ({
    key: channel.id,
    columns: [
      {
        key: 'type',
        value: <ChannelIconByType type={channel.protocol} avatarUrl={channel.avatar_url} />,
        sx: COLUMN_WIDTHS.type,
      },
      {
        key: 'channelId',
        value: (
          <CustomTypography
            variant="caption"
            sx={{ fontFamily: 'monospace', userSelect: 'all' }}
          >
            {channel.support_id ?? '-'}
          </CustomTypography>
        ),
        sx: COLUMN_WIDTHS.channelId,
      },
      {
        key: 'name',
        value: (
          <CustomTypography sx={{ fontWeight: 500 }}>
            {getChannelName(channel)}
          </CustomTypography>
        ),
        sx: COLUMN_WIDTHS.name,
      },
      {
        key: 'status',
        value: (
          <ChannelListStatusCell channel={channel} />
        ),
        sx: COLUMN_WIDTHS.status,
      },
      {
        key: 'subscription',
        value: renderTariff(channel),
        sx: COLUMN_WIDTHS.subscription,
      },
      {
        key: 'authorize',
        value: (
          <ChannelListActionButton
            channel={channel}
            canAuthorize={canAuthorizeChannelProtocol(channel.protocol)}
            onAuthorize={handleAuthButtonClick}
            onPaySubscription={onPaySubscription}
            onRenewSubscription={onRenewSubscription}
          />
        ),
        sx: { ...COLUMN_WIDTHS.authorize, paddingRight: '12px' },
      },
      {
        key: 'isActive',
        value: <ActiveSwitcher channel={channel} />,
        sx: { ...COLUMN_WIDTHS.isActive, paddingRight: '4px', paddingLeft: '12px' },
      },
      {
        key: 'actions',
        value: renderActions(channel),
        sx: { ...COLUMN_WIDTHS.actions, align: 'right', paddingLeft: '4px' },
      },
    ],
  }));

  return (
    <>
      <Table data={data} columns={getColumns(t)} />

      {selectedChannel ? (
        <>
          <WhatsAppPersonalAuthDialog
            open={openIsWhatsAppQrDialog}
            onClose={handleCloseWhatsAppQrDialog}
            onAuthorize={handleCloseWhatsAppQrDialog}
            channel={selectedChannel}
          />
          <CreateTgBotDialog
            open={openIsTgBotTokenDialog}
            onClose={handleCloseTgBotTokenDialog}
            onCreated={handleCloseTgBotTokenDialog}
            channelId={selectedChannel.id}
          />
          <MaxAuthDialog
            open={openIsMaxBotTokenDialog}
            onClose={handleCloseMaxBotTokenDialog}
            onCreated={handleCloseMaxBotTokenDialog}
            channelId={selectedChannel.id}
          />
          <MaxPersonalAuthDialog
            open={openIsMaxPersonalDialog}
            onClose={handleCloseMaxPersonalDialog}
            onAuthorize={handleCloseMaxPersonalDialog}
            channel={selectedChannel}
          />
          <TelegramPersonalAuthDialog
            open={openIsTgPersonalAuthMethodDialog}
            onClose={handleCloseTgPersonalAuthMethodDialog}
            onAuthorize={handleCloseTgPersonalAuthMethodDialog}
            channel={selectedChannel}
          />
          <AvitoConnectForm
            open={openIsAvitoDialog}
            onClose={handleCloseAvitoDialog}
            onAuthorize={handleCloseAvitoDialog}
            channel={selectedChannel}
          />
          <WildberriesConnectForm
            open={openIsWildberriesDialog}
            onClose={handleCloseWildberriesDialog}
            onAuthorize={handleCloseWildberriesDialog}
            channel={selectedChannel}
          />
          <OzonConnectForm
            open={openIsOzonDialog}
            onClose={handleCloseOzonDialog}
            onAuthorize={handleCloseOzonDialog}
            channel={selectedChannel}
          />
          <VkAuthDialog
            open={openIsVkDialog}
            onClose={handleCloseVkDialog}
            onCreated={handleCloseVkDialog}
            channelId={selectedChannel.id}
          />
        </>
      ) : null}
    </>
  );
};

type ChannelListStatusCellProps = {
  channel: IChannel;
};

const ChannelListStatusCell = ({ channel }: ChannelListStatusCellProps) => {
  const { data, isLoading, isError } = useChannelSubscriptionState(channel.id);

  return (
    <ChannelStatus
      status={channel.status}
      protocol={channel.protocol}
      isActive={channel.is_active}
      subscriptionEndDate={data?.end_date}
      subscriptionUnavailable={!isLoading && (isError || !data)}
      variant="list"
    />
  );
};

type ChannelListActionButtonProps = {
  channel: IChannel;
  canAuthorize: boolean;
  onAuthorize: (channel: IChannel) => void;
  onPaySubscription?: (channel: IChannel) => void;
  onRenewSubscription?: (channel: IChannel) => void;
};

const ChannelListActionButton = ({
  channel,
  canAuthorize,
  onAuthorize,
  onPaySubscription,
  onRenewSubscription,
}: ChannelListActionButtonProps) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useChannelSubscriptionState(channel.id);

  const action = getChannelPrimaryAction({
    status: channel.status,
    isActive: channel.is_active,
    subscriptionEndDate: data?.end_date,
    subscriptionUnavailable: !isLoading && (isError || !data),
  });
  const paymentLink = data?.payment_link ?? data?.paymentLink;
  if (channel.status === CHANNEL_STATUS.PENDING_PAYMENT && isHttpsPaymentLink(paymentLink)) {
    return (
      <ListItemButton
        component="a"
        href={paymentLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('billing:payment.actions.openInvoice')}
      </ListItemButton>
    );
  }

  if (action === 'pay' && onPaySubscription) {
    return (
      <ListItemButton onClick={() => onPaySubscription(channel)}>
        {t('billing:payment.actions.pay')}
      </ListItemButton>
    );
  }

  if (action === 'renew' && onRenewSubscription) {
    return (
      <ListItemButton onClick={() => onRenewSubscription(channel)}>
        {t('billing:extendSubscription')}
      </ListItemButton>
    );
  }

  if (action === 'authorize' && canAuthorize) {
    return (
      <ListItemButton onClick={() => onAuthorize(channel)}>
        {t('channels:listTable.auth')}
      </ListItemButton>
    );
  }

  return null;
};
